import { messaging, getToken, onMessage, deleteToken } from './firebaseConfig';
import { registerDeviceToken, deactivateToken } from '../apiService/deviceTokenService';

const VAPID_KEY = 'BCfrM5LkEY3mT8kdP1fo1ySeiy-C-0rsuRQOT0n48Nv-XVfuHmfK3YWaeOvZvjP-3mqqX008Jz3kQaMGTFPkIKs';

/** Same tab: skip repeat POST /register on every refresh if token unchanged. Cleared when sessionStorage clears (e.g. logout). */
const FCM_BACKEND_REGISTERED_TOKEN_KEY = 'fcm_backend_registered_token';

function shouldSkipBackendRegister(token) {
  if (!token || typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(FCM_BACKEND_REGISTERED_TOKEN_KEY) === token;
}

function markBackendRegistered(token) {
  if (token && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(FCM_BACKEND_REGISTERED_TOKEN_KEY, token);
  }
}
 
export const isFlutterWebView = () => typeof window !== 'undefined' && !!window.flutter_inappwebview;
 
export const requestNotificationPermission = async () => {
  try {
    if (isFlutterWebView()) {
      return true;
    }
    const permission = await Notification.requestPermission();
    console.log('[FCM] Notification permission:', permission === 'granted' ? 'granted' : 'denied');
    return permission === 'granted';
  } catch (error) {
    console.error('[FCM] Error requesting notification permission:', error);
    return false;
  }
};
 
export const getFCMToken = async () => {
  try {
    if (isFlutterWebView()) {
      const token = localStorage.getItem('FCM_TOKEN_FLUTTER');
      if (token) return token;
      const fromHandler = await window.flutter_inappwebview?.callHandler('getFCMToken');
      return fromHandler || null;
    }
    if (!window.isSecureContext) {
      console.error('[FCM] ❌ Not in a secure context (HTTPS required except for localhost)');
      return null;
    }
    if (!('serviceWorker' in navigator)) {
      console.error('[FCM] ❌ Service Worker not supported in this browser');
      return null;
    }
    console.log('[FCM] ✅ Secure context and Service Worker support confirmed');
    console.log('[FCM] Current origin:', window.location.origin);
    let registration;
    try {
      console.log('[FCM] Registering service worker...');
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
        updateViaCache: 'none'
      });
      console.log('[FCM] ✅ Service worker registered successfully');
      console.log('[FCM] Service worker scope:', registration.scope);
      await navigator.serviceWorker.ready;
      console.log('[FCM] ✅ Service worker is ready');
    } catch (swError) {
      console.error('[FCM] ❌ Service worker registration failed:', swError);
      return null;
    }
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    console.log('[FCM] FCM token:', currentToken ? 'generated' : 'not generated');
    return currentToken || null;
  } catch (error) {
    console.error('[FCM] Error retrieving FCM token:', error);
    return null;
  }
};
 
const syncDeviceDataFromFlutter = async () => {
  if (!isFlutterWebView()) return;
  try {
    const deviceType = await window.flutter_inappwebview?.callHandler('getDeviceType');
    const deviceName = await window.flutter_inappwebview?.callHandler('getDeviceName');
    if (deviceType) localStorage.setItem('device_type', deviceType);
    if (deviceName) localStorage.setItem('device_name', deviceName);
  } catch (e) {
    console.warn('[FCM] Could not sync device data from Flutter:', e);
  }
};
 
export const registerFCMToken = async () => {
  try {
    if (isFlutterWebView()) {
      await syncDeviceDataFromFlutter();
      const token = localStorage.getItem('FCM_TOKEN_FLUTTER') || await window.flutter_inappwebview?.callHandler('getFCMToken');
      if (!token) {
        console.log('[FCM] Waiting for Flutter token...');
        return null;
      }
      const deviceId = getDeviceId();
      const deviceData = {
        token: token,
        deviceType: localStorage.getItem('device_type') || 'ANDROID',
        deviceName: localStorage.getItem('device_name') || 'RIL OneApp',
        deviceId: deviceId
      };
      if (shouldSkipBackendRegister(token)) {
        sessionStorage.setItem('fcm_token', token);
        console.log('[FCM] Skipping device register API (already sent this session for this token)');
        return token;
      }
      await registerDeviceToken(deviceData);
      sessionStorage.setItem('fcm_token', token);
      markBackendRegistered(token);
      console.log('[FCM] Flutter FCM token registered successfully');
      return token;
    }
    if (!VAPID_KEY || VAPID_KEY === 'YOUR_VAPID_KEY_HERE') {
      console.error('[FCM] VAPID key not configured');
      return null;
    }
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) return null;
    const token = await getFCMToken();
    if (token) {
      const deviceId = getDeviceId();
      const deviceData = {
        token: token,
        deviceType: 'WEB',
        deviceName: navigator.userAgent,
        deviceId: deviceId
      };
      if (shouldSkipBackendRegister(token)) {
        sessionStorage.setItem('fcm_token', token);
        console.log('[FCM] Skipping device register API (already sent this session for this token)');
        return token;
      }
      await registerDeviceToken(deviceData);
      sessionStorage.setItem('fcm_token', token);
      markBackendRegistered(token);
      console.log('[FCM] FCM token registered successfully');
      return token;
    }
    return null;
  } catch (error) {
    console.error('[FCM] Error registering FCM token:', error);
    return null;
  }
};
 
export const registerFCMTokenWithRetry = async (maxRetries = 3, delayMs = 2000) => {
  if (!isFlutterWebView()) {
    return registerFCMToken();
  }
  for (let i = 0; i < maxRetries; i++) {
    const token = await registerFCMToken();
    if (token) return token;
    if (i < maxRetries - 1) {
      console.log(`[FCM] Retry ${i + 1}/${maxRetries - 1} in ${delayMs}ms...`);
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return null;
};
 
/**
 * Clears the FCM token on logout so the next user on this browser/device
 * gets a fresh token instead of inheriting the previous user's.
 * - Deactivates the token on the backend (removes user→token mapping).
 * - Calls Firebase deleteToken() so getToken() returns a brand-new token next time.
 */
export const unregisterFCMToken = async () => {
  try {
    const token =
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('fcm_token')) ||
      (isFlutterWebView() && typeof localStorage !== 'undefined' && localStorage.getItem('FCM_TOKEN_FLUTTER')) ||
      null;

    if (token) {
      try {
        await deactivateToken(token);
        console.log('[FCM] Token deactivated on backend');
      } catch (e) {
        console.warn('[FCM] Backend deactivate failed:', e);
      }
    }

    // Firebase deleteToken only applies to web FCM. In Flutter WebView the
    // native side owns the token lifecycle, so skip it there.
    if (!isFlutterWebView() && messaging) {
      try {
        await deleteToken(messaging);
        console.log('[FCM] Firebase token deleted locally');
      } catch (e) {
        console.warn('[FCM] Firebase deleteToken failed:', e);
      }
    }

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('fcm_token');
      sessionStorage.removeItem(FCM_BACKEND_REGISTERED_TOKEN_KEY);
    }
  } catch (e) {
    console.error('[FCM] Error unregistering FCM token:', e);
  }
};

export const onMessageListener = (callback) => {
  if (isFlutterWebView()) {
    return;
  }
  return onMessage(messaging, (payload) => {
    try {
      console.log('[FCM] Message received:', payload?.notification?.title || payload?.data?.title || 'notification');
      callback(payload);
    } catch (e) {
      console.error('[FCM] Error in message listener:', e);
    }
  });
};
 
const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = (isFlutterWebView() ? 'android_' : 'web_') + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};